import { useStore } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import { GoalStatus, GoalTimeline } from "~/../drizzle/schema";
import { useAppForm } from "~/components/form/app-form";
import {
  FieldErrorsProvider,
  useFieldError,
  zodErrorLookup,
} from "~/components/form/fields/field-errors";
import { Button } from "~/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerTitle,
  SideDrawerBody,
  SideDrawerContent,
  SideDrawerFooter,
  SideDrawerHeader,
} from "~/components/ui/drawer";
import { FormField } from "~/components/ui/form-field";
import { GOAL_TIMELINE_OPTIONS } from "~/components/ui/goal-badges";
import { Input } from "~/components/ui/input";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { cn } from "~/lib/cn";
import {
  decryptResourceFields,
  useCoachShareTargets,
  useEncryptOnSave,
  useGoalShareTargets,
} from "~/lib/crypto";
import type { KeyManager, ShareTarget } from "~/lib/crypto";
import { generateGoalShareSlugFn } from "~/lib/functions/goal-of-life";
import {
  categoryQueryKeys,
  useCategoriesOfLife,
} from "~/lib/queries/categoryQuery";
import {
  useCreateGoalOfLife,
  useUpdateGoalOfLife,
} from "~/lib/queries/goalOfLifeQuery";
import {
  milestonesByGoalQueryOptions,
  useSyncMilestones,
} from "~/lib/queries/milestoneQuery";
import {
  GOAL_DESCRIPTION_MAX_LENGTH,
  GOAL_TITLE_MAX_LENGTH,
  GoalFormSchema,
  MILESTONE_TITLE_MAX_LENGTH,
  type GoalFormData,
} from "~/lib/schemas/goal-of-life";
import { LIFE_CATEGORY_SUGGESTIONS } from "~/lib/shared/constants/life-category-suggestions";
import { createAndUploadCategoryFile } from "~/lib/upload-file";

import type { LifeGoal } from "./goal-groups";
import { useCategoryName } from "./goal-list";

/** The goal being edited, with the text the panel decrypted. */
export type GoalToEdit = {
  goal: LifeGoal;
  categoryId: string;
  title: string;
  description: string | null;
};

/** A chip that is on or off, as in a segmented control. */
function ChoiceChip({
  selected,
  onSelect,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      size="sm"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        "max-w-full",
        selected &&
          "border-purple-200 bg-purple-50 text-purple-900 hover:bg-purple-100",
      )}
    >
      {children}
    </Button>
  );
}

/**
 * A category's decrypted name, as plain text: no `title` attribute, which
 * click tracking (Sentry breadcrumbs) would read and send.
 */
function CategoryLabel({
  category,
  keyManager,
}: {
  category: { id: string; encryptedName: string | null };
  keyManager: KeyManager | null;
}) {
  const { name } = useCategoryName({ category, keyManager });
  return <>{name}</>;
}

const NEW_CATEGORY = "new";
const SUGGESTION = "suggestion:";

/**
 * Picks one of the client's categories, or names a new one. A client without
 * categories picks from the Vision suggestions, so her first goal never
 * dead-ends; the category then appears on her Vision too.
 *
 * Radio buttons, not chip buttons: a radio holds no text, and the Meta pixel
 * (`__root.tsx`) reports the text of every button clicked. The names are
 * decrypted text, so they sit in each radio's label.
 */
function CategoryPicker({
  categories,
  keyManager,
  categoryId,
  newCategoryName,
  onChange,
}: {
  categories: ReadonlyArray<{ id: string; encryptedName: string | null }>;
  keyManager: KeyManager | null;
  categoryId: string;
  newCategoryName: string;
  onChange: (value: { categoryId: string; newCategoryName: string }) => void;
}) {
  const pickError = useFieldError("categoryId");
  const nameError = useFieldError("newCategoryName");
  const suggestions: readonly string[] =
    categories.length === 0 ? LIFE_CATEGORY_SUGGESTIONS : [];
  const [namingOther, setNamingOther] = useState(
    !categoryId && !!newCategoryName && !suggestions.includes(newCategoryName),
  );
  const selected = namingOther
    ? NEW_CATEGORY
    : categoryId || (newCategoryName ? `${SUGGESTION}${newCategoryName}` : "");

  const select = (value: string) => {
    setNamingOther(value === NEW_CATEGORY);
    if (value === NEW_CATEGORY)
      onChange({ categoryId: "", newCategoryName: "" });
    else if (value.startsWith(SUGGESTION))
      onChange({
        categoryId: "",
        newCategoryName: value.slice(SUGGESTION.length),
      });
    else onChange({ categoryId: value, newCategoryName: "" });
  };

  return (
    <FormField label="Category" required error={pickError ?? nameError}>
      <div className="space-y-3">
        {categories.length === 0 && (
          <p className="text-text-tertiary text-sm">
            You have no categories yet. Pick one to start; it also appears on
            your Vision.
          </p>
        )}
        <RadioGroup
          aria-label="Category"
          value={selected}
          onValueChange={select}
          className="flex flex-wrap gap-x-6 gap-y-3"
        >
          {categories.map((category) => (
            <RadioGroupItem key={category.id} value={category.id}>
              <CategoryLabel category={category} keyManager={keyManager} />
            </RadioGroupItem>
          ))}
          {suggestions.map((name) => (
            <RadioGroupItem
              key={name}
              value={`${SUGGESTION}${name}`}
              label={name}
            />
          ))}
          <RadioGroupItem
            value={NEW_CATEGORY}
            label={categories.length === 0 ? "Other" : "New category"}
          />
        </RadioGroup>
        {namingOther && (
          <Input
            autoFocus
            aria-label="New category name"
            placeholder="Name the category"
            value={newCategoryName}
            onValueChange={(name) =>
              onChange({ categoryId: "", newCategoryName: name })
            }
            className="mb-0"
          />
        )}
      </div>
    </FormField>
  );
}

/**
 * Up to a few first steps for a new goal. One empty field always waits at
 * the end; Enter in it moves to the next.
 */
function FirstStepsField({
  steps,
  onChange,
}: {
  steps: string[];
  onChange: (steps: string[]) => void;
}) {
  const error = useFieldError("firstSteps");
  const rows = steps.at(-1) === "" ? steps : [...steps, ""];
  const update = (index: number, value: string) => {
    const next = [...rows];
    next[index] = value.slice(0, MILESTONE_TITLE_MAX_LENGTH);
    onChange(next);
  };

  return (
    <FormField label="First steps (optional)" error={error}>
      <ul className="space-y-2">
        {rows.map((step, index) => {
          const isLast = index === rows.length - 1;
          return (
            // Rows only grow at the end and shrink by their own button.
            <li key={index} className="flex items-center gap-2">
              <Input
                aria-label={`Step ${index + 1}`}
                placeholder="Add a step"
                value={step}
                onValueChange={(value) => update(index, value)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  const inputs = event.currentTarget
                    .closest("ul")
                    ?.querySelectorAll("input");
                  inputs?.[index + 1]?.focus();
                }}
                className="mb-0"
              />
              {!isLast && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove step ${index + 1}`}
                  onClick={() => onChange(rows.filter((_, i) => i !== index))}
                >
                  <X aria-hidden />
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </FormField>
  );
}

/**
 * The new goal and edit goal form. Every text is encrypted in the browser
 * before it is saved, with keys for the coaches the goals are shared with.
 * Editing changes the goal's text and place, never its state.
 */
export function GoalFormDrawer({
  open,
  onOpenChange,
  goalToEdit,
  initialCategoryId,
  keyManager,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goalToEdit?: GoalToEdit | null;
  /** The category a new goal starts in, e.g. from a category's "Add goal". */
  initialCategoryId?: string;
  keyManager: KeyManager | null;
}) {
  // Read here, where the page mounts the drawer, so the categories load
  // with the page and opening the form never waits on them.
  const { data: categories } = useCategoriesOfLife();
  return (
    <Drawer direction="right" open={open} onOpenChange={onOpenChange}>
      <SideDrawerContent>
        {open && (
          <GoalForm
            categories={categories}
            goalToEdit={goalToEdit ?? null}
            initialCategoryId={initialCategoryId}
            keyManager={keyManager}
            onDone={() => onOpenChange(false)}
          />
        )}
      </SideDrawerContent>
    </Drawer>
  );
}

function GoalForm({
  categories,
  goalToEdit,
  initialCategoryId,
  keyManager,
  onDone,
}: {
  categories: ReadonlyArray<{ id: string; encryptedName: string | null }>;
  goalToEdit: GoalToEdit | null;
  initialCategoryId?: string;
  keyManager: KeyManager | null;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const { encryptFields } = useEncryptOnSave(keyManager);
  const coachShareTargets = useCoachShareTargets();
  const goalShareTargets = useGoalShareTargets();
  const createGoal = useCreateGoalOfLife();
  const updateGoal = useUpdateGoalOfLife();
  const syncSteps = useSyncMilestones();
  const [attempted, setAttempted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const defaultValues: GoalFormData = {
    title: goalToEdit?.title ?? "",
    categoryId: goalToEdit?.categoryId ?? initialCategoryId ?? "",
    newCategoryName: "",
    timeline: goalToEdit?.goal.timeline ?? GoalTimeline.QUARTER,
    description: goalToEdit?.description ?? "",
    firstSteps: [],
  };
  const form = useAppForm({ defaultValues });
  const values = useStore(form.store, (state) => state.values);
  const parsed = GoalFormSchema.safeParse(values);

  const [newGoalId] = useState(() => uuidv4());
  // A category this form created on a save that then failed, reused on retry.
  const createdCategory = useRef<{ name: string; id: string } | null>(null);

  /** A new category is created first, so the goal can sit in it; a retry reuses it. */
  const categoryFor = async ({
    newCategoryName,
  }: {
    newCategoryName: string;
  }): Promise<string> => {
    if (createdCategory.current?.name === newCategoryName) {
      return createdCategory.current.id;
    }
    const id = await createCategory(newCategoryName);
    createdCategory.current = { name: newCategoryName, id };
    return id;
  };

  const createCategory = async (name: string): Promise<string> => {
    const categoryId = uuidv4();
    const encrypted = await encryptFields(
      { name, shortDescription: "" },
      await coachShareTargets("vision"),
    );
    await createAndUploadCategoryFile({
      categoryId,
      encryptedName: encrypted.fields.name,
      encryptedShortDescription: encrypted.fields.shortDescription,
      iv: encrypted.iv,
      keyGrants: encrypted.keyGrants,
    });
    await queryClient.invalidateQueries({ queryKey: categoryQueryKeys.all });
    return categoryId;
  };

  /** Each first step is its own goal with its own key. */
  const saveFirstSteps = async (
    goalId: string,
    steps: string[],
    coaches: readonly ShareTarget[],
  ) => {
    const milestones = await Promise.all(
      steps.map(async (title) => {
        const encrypted = await encryptFields({ title }, coaches);
        return {
          id: uuidv4(),
          status: GoalStatus.PLANNED,
          newTitle: {
            encryptedTitle: encrypted.fields.title,
            iv: encrypted.iv,
            keyGrants: encrypted.keyGrants,
          },
        };
      }),
    );
    await syncSteps.mutateAsync({ goalId, milestones });
  };

  /** The public link shows a plain copy; refresh it with the edited text and the steps' titles. */
  const refreshShareCopy = async (
    goalId: string,
    data: { title: string; description: string },
    categoryId: string,
    newCategoryName: string,
  ) => {
    if (!keyManager) return;
    const category = categories.find((c) => c.id === categoryId);
    const categoryName =
      newCategoryName ||
      (category
        ? ((
            await decryptResourceFields({
              resourceType: "categoryOfLife",
              resourceId: category.id,
              keyManager,
              fields: { name: { encrypted: category.encryptedName } },
            })
          ).name ?? "")
        : "");
    const steps = await queryClient.fetchQuery(
      milestonesByGoalQueryOptions({ goalId, viewer: "owner", keyManager }),
    );
    await generateGoalShareSlugFn({
      data: {
        goalId,
        sharePlaintext: {
          title: data.title,
          description: data.description || null,
          categoryName,
          // A title this browser could not decrypt is not published.
          steps: steps
            .filter((step) => step.isReadable)
            .map(({ id, title }) => ({ id, title })),
        },
      },
    });
  };

  const save = async () => {
    setAttempted(true);
    if (!parsed.success) return;
    if (!keyManager) {
      toast.error("Unlock with your PIN to save.");
      return;
    }
    const data = parsed.data;
    setIsSaving(true);
    try {
      const categoryId = data.categoryId || (await categoryFor(data));
      // A new goal's id is made once per form, so it is created once, with
      // its keys, however often the client retries. Each save makes new keys;
      // the coaches the goals are shared with, and the coach of each program
      // the goal is in, get theirs in the same save.
      const goalId = goalToEdit?.goal.id ?? newGoalId;
      const coaches = await goalShareTargets(goalId);
      const encrypted = await encryptFields(
        { title: data.title, description: data.description ?? "" },
        coaches,
      );
      const goal = {
        id: goalId,
        categoryId,
        timeline: data.timeline,
        encryptedTitle: encrypted.fields.title,
        encryptedDescription: encrypted.fields.description,
        iv: encrypted.iv,
        keyGrants: encrypted.keyGrants,
      };

      if (goalToEdit) {
        // The saved row, not the one the form opened from, says whether the
        // goal has a public link: it may have been shared since.
        const updated = await updateGoal.mutateAsync(goal);
        if (updated.shareSlug) {
          // Last, and on its own: the goal is saved even when the public
          // copy cannot be refreshed.
          await refreshShareCopy(
            goalId,
            { title: data.title, description: data.description ?? "" },
            categoryId,
            data.newCategoryName,
          ).catch((error: unknown) => {
            console.error("Failed to refresh the goal's public link", {
              goalId,
              error,
            });
            toast.warning("Goal saved, but its public link was not updated.");
          });
        }
      } else {
        await createGoal.mutateAsync(goal);
        const steps = data.firstSteps.filter(Boolean);
        if (steps.length > 0) {
          // The goal exists now; a failed step save must not invite a retry
          // that would create it again.
          const saved = await saveFirstSteps(goalId, steps, coaches).then(
            () => true,
            (error: unknown) => {
              console.error("Failed to save a new goal's first steps", {
                goalId,
                error,
              });
              return false;
            },
          );
          if (!saved) {
            toast.warning(
              "Goal created, but its first steps were not saved. Add them in the goal.",
            );
            onDone();
            return;
          }
        }
      }
      toast.success(goalToEdit ? "Goal updated" : "Goal created");
      onDone();
    } catch (error) {
      console.error("Failed to save goal", { error });
      toast.error(
        goalToEdit
          ? "The goal was not saved. Try again."
          : "The goal was not created. Try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FieldErrorsProvider
      value={zodErrorLookup(
        parsed.success ? [] : parsed.error.issues,
        attempted,
      )}
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
        className="flex min-h-0 flex-1 flex-col"
      >
        <SideDrawerHeader>
          <DrawerTitle className="font-heading text-xl font-medium text-gray-900">
            {goalToEdit ? "Edit goal" : "New goal"}
          </DrawerTitle>
        </SideDrawerHeader>

        <SideDrawerBody className="space-y-6 py-6">
          <form.AppField name="title">
            {(field) => (
              <field.TextField
                label="What do you want to reach?"
                placeholder="Run a half marathon"
                required
                maxLength={GOAL_TITLE_MAX_LENGTH}
              />
            )}
          </form.AppField>

          <CategoryPicker
            categories={categories}
            keyManager={keyManager}
            categoryId={values.categoryId}
            newCategoryName={values.newCategoryName}
            onChange={({ categoryId, newCategoryName }) => {
              form.setFieldValue("categoryId", categoryId);
              form.setFieldValue("newCategoryName", newCategoryName);
            }}
          />

          <FormField label="Timeframe">
            <div
              role="group"
              aria-label="Timeframe"
              className="flex flex-wrap gap-2"
            >
              {GOAL_TIMELINE_OPTIONS.map((option) => (
                <ChoiceChip
                  key={option.id}
                  selected={values.timeline === option.id}
                  onSelect={() => form.setFieldValue("timeline", option.id)}
                >
                  {option.label}
                </ChoiceChip>
              ))}
            </div>
          </FormField>

          <form.AppField name="description">
            {(field) => (
              <field.RichTextField
                label="Why it matters (optional)"
                placeholder="A sentence for future you…"
                maxLength={GOAL_DESCRIPTION_MAX_LENGTH}
              />
            )}
          </form.AppField>

          {!goalToEdit && (
            <FirstStepsField
              steps={values.firstSteps}
              onChange={(steps) => form.setFieldValue("firstSteps", steps)}
            />
          )}
        </SideDrawerBody>

        <SideDrawerFooter>
          <DrawerClose asChild>
            <Button type="button" disabled={isSaving}>
              Cancel
            </Button>
          </DrawerClose>
          <Button type="submit" variant="primary" loading={isSaving}>
            {goalToEdit ? "Save changes" : "Create goal"}
          </Button>
        </SideDrawerFooter>
      </form>
    </FieldErrorsProvider>
  );
}
